import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FiX, FiCheck, FiChevronLeft, FiChevronRight, FiPaperclip, FiFileText, FiMessageSquare,
  FiAlertTriangle, FiLock, FiUnlock, FiList, FiExternalLink, FiPlus, FiTrash2,
} from 'react-icons/fi';
import css from './OnboardingTela.module.css';
import useEsc from '../../../../hooks/useEsc';
import { buscarOnboarding, salvarItemOnboarding, reunioesDoMes, enviarAnexoOnboarding } from '../../../../services/onboarding';
import { abrirArquivo } from '../../../../services/arquivos';
import { EditorMinuta } from '../../leads/components/MinutasLead';
import { fluxoPorId } from '../../leads/fluxos';

/* ── Onboarding novo (leads do fluxo novo) ──
   Cabeçalho fixo com os indicadores, trilha das etapas (uma aberta por
   vez) e itens em linhas compactas: status + nome + ação + quem/quando +
   observação recolhível. Só o gerente executa; os demais só veem. */

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const dataBr = (iso) => { if (!iso) return ''; const [a, m, d] = String(iso).slice(0, 10).split('-'); return `${d}/${m}/${a}`; };
const quando = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '');
const hojeIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const moeda = (v) => (v === null || v === undefined || v === '' ? '' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));

/* ── linha compacta de item ── */
function Linha({ feito, alerta, titulo, sub, meta, acoes, obs, onObs, podeEditar, children }) {
  const [abrirObs, setAbrirObs] = useState(false);
  return (
    <div className={css.linha}>
      <div className={css.linhaTopo}>
        <span className={`${css.status} ${feito ? css.statusOk : alerta ? css.statusAlerta : ''}`}>
          {feito ? <FiCheck size={12} /> : alerta ? <FiAlertTriangle size={11} /> : null}
        </span>
        <div className={css.linhaTexto}>
          <span className={css.linhaTitulo}>{titulo}</span>
          {sub && <span className={css.linhaSub}>{sub}</span>}
          {meta && <span className={css.linhaMeta}>{meta}</span>}
        </div>
        <div className={css.linhaAcoes}>
          {acoes}
          {onObs && (
            <button type="button" className={`${css.btnObs} ${obs ? css.btnObsCom : ''}`} title={obs ? `Observação: ${obs}` : 'Observação'}
              onClick={() => setAbrirObs(v => !v)}><FiMessageSquare size={13} /></button>
          )}
        </div>
      </div>
      {children && <div className={css.linhaCorpo}>{children}</div>}
      {onObs && abrirObs && (
        <textarea className={css.obs} defaultValue={obs || ''} rows={2} placeholder="Observação" disabled={!podeEditar}
          onBlur={e => { if ((e.target.value.trim() || null) !== (obs || null)) onObs(e.target.value); }} />
      )}
    </div>
  );
}

const Secao = ({ titulo, sub, children }) => (
  <section className={css.secao}>
    <header className={css.secaoTopo}><h3>{titulo}</h3>{sub && <span>{sub}</span>}</header>
    {children}
  </section>
);

/* ── calendário do mês: dias com reunião de onboarding marcados ── */
function Calendario({ valor, onEscolher, desabilitado }) {
  const base = valor ? valor.slice(0, 7) : hojeIso().slice(0, 7);
  const [mes, setMes] = useState(base);
  const [ocupados, setOcupados] = useState([]);
  useEffect(() => { reunioesDoMes(mes).then(r => setOcupados(r.items || [])).catch(() => setOcupados([])); }, [mes]);
  const [a, m] = mes.split('-').map(Number);
  const primeiro = new Date(a, m - 1, 1).getDay();
  const dias = new Date(a, m, 0).getDate();
  const mudar = (n) => { const d = new Date(a, m - 1 + n, 1); setMes(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`); };
  const celulas = [...Array(primeiro).fill(null), ...Array.from({ length: dias }, (_, i) => i + 1)];
  const hoje = hojeIso();
  return (
    <div className={css.cal}>
      <div className={css.calTopo}>
        <button type="button" onClick={() => mudar(-1)}><FiChevronLeft size={14} /></button>
        <strong>{MESES[m - 1]} {a}</strong>
        <button type="button" onClick={() => mudar(1)}><FiChevronRight size={14} /></button>
      </div>
      <div className={css.calGrade}>
        {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((d, i) => <span key={i} className={css.calSemana}>{d}</span>)}
        {celulas.map((d, i) => {
          if (!d) return <span key={i} />;
          const iso = `${mes}-${String(d).padStart(2, '0')}`;
          const doDia = ocupados.filter(o => o.data === iso);
          return (
            <button key={i} type="button" disabled={desabilitado}
              className={`${css.calDia} ${iso === valor ? css.calDiaSel : ''} ${iso === hoje ? css.calHoje : ''}`}
              title={doDia.map(o => `${o.hora || ''} ${o.empresa || o.nome}`).join('\n') || undefined}
              onClick={() => onEscolher(iso)}>
              {d}{doDia.length > 0 && <i className={css.calPonto} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function OnboardingTela({ leadId, onFechar, onAbrirChecklist }) {
  const [d, setD] = useState(null);
  const [erro, setErro] = useState('');
  const [aberta, setAberta] = useState(null);
  const [minutaAberta, setMinutaAberta] = useState(null);
  const [salvando, setSalvando] = useState(null);
  const navigate = useNavigate();
  const arquivoRef = useRef(null);
  const alvoArquivo = useRef(null);
  useEsc(onFechar, !minutaAberta);

  const carregar = useCallback(() => buscarOnboarding(leadId).then(r => {
    setD(r);
    setAberta(a => a ?? (r.etapas.find(e => !e.concluida)?.id || r.etapas[r.etapas.length - 1].id));
  }).catch(e => setErro(e.message)), [leadId]);
  useEffect(() => { carregar(); }, [carregar]);
  /* voltou pra aba do Onboard (a procuração pode ter sido salva lá): recarrega */
  const location = useLocation();
  useEffect(() => { if (location.pathname === '/app/clientes/onboard') carregar(); }, [location.pathname, carregar]);

  if (!d) {
    return (
      <div className={css.fundo}><div className={css.tela}>
        <div className={css.corpo}>{erro ? <p className={css.erro}>{erro}</p> : <p className={css.vazio}>Carregando onboarding...</p>}</div>
      </div></div>
    );
  }

  const pode = d.pode_executar;
  const it = (c) => d.itens[c] || {};
  const metaDe = (c) => { const x = d.itens[c]; return x?.status ? `${x.feito_por || ''} · ${quando(x.feito_em)}` : null; };
  async function salvar(chave, corpo) {
    setSalvando(chave); setErro('');
    try { setD(await salvarItemOnboarding(leadId, chave, corpo)); } catch (e) { setErro(e.message); }
    setSalvando(null);
  }
  const obsDe = (c) => ({ obs: it(c).observacao, onObs: (t) => salvar(c, { observacao: t }), podeEditar: pode });
  function anexar(chave, extra = {}) {
    alvoArquivo.current = { chave, extra };
    arquivoRef.current.value = '';
    arquivoRef.current.click();
  }
  async function aoEscolherArquivo(e) {
    const file = e.target.files?.[0];
    const alvo = alvoArquivo.current;
    if (!file || !alvo) return;
    setSalvando(alvo.chave); setErro('');
    try { const a = await enviarAnexoOnboarding(leadId, file); setD(await salvarItemOnboarding(leadId, alvo.chave, { ...a, ...alvo.extra })); } catch (err) { setErro(err.message); }
    setSalvando(null);
  }
  const Anexo = ({ chave, extra }) => (it(chave).arquivo_path ? (
    <button type="button" className={css.btnLink} onClick={() => abrirArquivo(it(chave).arquivo_path).catch(e2 => setErro(e2.message))}>
      <FiFileText size={12} /> {it(chave).arquivo_nome || 'anexo'}
    </button>
  ) : (
    <button type="button" className={css.btnSec} disabled={!pode || salvando === chave} onClick={() => anexar(chave, extra)}>
      <FiPaperclip size={12} /> Anexar
    </button>
  ));
  const tituloBloqueio = pode ? undefined : 'Só o gerente executa o onboarding';

  const lead = d.lead;
  const fluxo = fluxoPorId(lead.tipo_fluxo);
  const etapa = d.etapas.find(e => e.id === aberta) || d.etapas[0];
  const indice = d.etapas.findIndex(e => e.id === etapa.id);
  const pendenciaCertidao = ['rfb', 'fgts', 'estadual', 'municipal'].some(c => it(`certidao:${c}`).status === 'pendencia');

  /* ── Etapa 1 ── */
  function etapa1() {
    const reuniao = it('reuniao');
    const r = reuniao.dados || {};
    return (
      <>
        <Secao titulo="Conferência dos contratos" sub="Prontos para assinatura no início da reunião">
          {d.minutas.length === 0 && <p className={css.aviso}><FiAlertTriangle size={13} /> Nenhuma minuta gerada no Leads. Gere em Leads › Documentos e Contrato.</p>}
          {d.minutas.map(m => {
            const c = `minuta:${m.id}`;
            const ok = it(c).status === 'conferido';
            return (
              <Linha key={m.id} feito={ok} titulo={m.titulo} sub={`Versão ${m.versao_atual} · ${m.status.replace('_', ' ')}`} meta={metaDe(c)} {...obsDe(c)}
                acoes={<>
                  <button type="button" className={css.btnSec} onClick={() => setMinutaAberta(m.id)}><FiExternalLink size={12} /> Abrir minuta</button>
                  <button type="button" className={ok ? css.btnOk : css.btnPri} disabled={!pode || salvando === c} title={tituloBloqueio}
                    onClick={() => salvar(c, { status: ok ? null : 'conferido' })}>{ok ? <><FiCheck size={12} /> Conferido</> : 'Marcar como conferido'}</button>
                </>} />
            );
          })}
        </Secao>

        <Secao titulo="Certidões negativas">
          {pendenciaCertidao && <p className={css.alerta}><FiAlertTriangle size={14} /> Informar cliente: possível cobrança adicional.</p>}
          <div className={css.grade2}>
            {[['rfb', 'Receita Federal (RFB)'], ['fgts', 'FGTS'], ['estadual', 'Estado'], ['municipal', 'Prefeitura']].map(([k, nome]) => {
              const c = `certidao:${k}`;
              const x = it(c);
              return (
                <Linha key={k} feito={x.status === 'regular'} alerta={x.status === 'pendencia'} titulo={nome} meta={metaDe(c)} {...obsDe(c)}
                  acoes={<Anexo chave={c} />}>
                  <div className={css.campos}>
                    <div className={css.segmento}>
                      {[['regular', 'Regular'], ['pendencia', 'Com pendência']].map(([v, l]) => (
                        <button key={v} type="button" disabled={!pode} title={tituloBloqueio}
                          className={x.status === v ? (v === 'regular' ? css.segOk : css.segAlerta) : ''}
                          onClick={() => salvar(c, { status: x.status === v ? null : v, dados: { ...(x.dados || {}), data: x.dados?.data || hojeIso() } })}>{l}</button>
                      ))}
                    </div>
                    <label className={css.campoMini}>Consulta
                      <input type="date" className={css.input} disabled={!pode} value={x.dados?.data || ''}
                        onChange={e => salvar(c, { dados: { ...(x.dados || {}), data: e.target.value } })} />
                    </label>
                  </div>
                </Linha>
              );
            })}
          </div>
        </Secao>

        <Secao titulo="1ª reunião e boas-vindas">
          <div className={css.reuniao}>
            <Calendario valor={r.data} desabilitado={!pode} onEscolher={(dia) => salvar('reuniao', { status: r.hora ? 'agendada' : null, dados: { ...r, data: dia } })} />
            <div className={css.reuniaoCampos}>
              <div className={css.grade2Campos}>
                <label className={css.campoMini}>Dia
                  <input className={css.input} value={r.data ? dataBr(r.data) : ''} readOnly placeholder="Escolha no calendário" />
                </label>
                <label className={css.campoMini}>Horário
                  <input type="time" className={css.input} disabled={!pode} value={r.hora || ''}
                    onChange={e => salvar('reuniao', { status: r.data && e.target.value ? 'agendada' : null, dados: { ...r, hora: e.target.value } })} />
                </label>
                <label className={css.campoMini}>Responsável pela reunião
                  <input className={css.input} disabled={!pode} defaultValue={r.responsavel || d.responsavel || ''}
                    onBlur={e => e.target.value !== (r.responsavel || '') && salvar('reuniao', { dados: { ...r, responsavel: e.target.value } })} />
                </label>
                <label className={css.campoMini}>Formato
                  <select className={css.input} disabled={!pode} value={r.formato || ''}
                    onChange={e => salvar('reuniao', { dados: { ...r, formato: e.target.value } })}>
                    <option value="">Selecione...</option>
                    <option value="presencial">Presencial</option>
                    <option value="online">Online</option>
                  </select>
                </label>
              </div>
              <Linha feito={!!r.data && !!r.hora} titulo="Reunião"
                sub={r.data && r.hora ? `${dataBr(r.data)} às ${r.hora}${r.formato ? ` · ${r.formato}` : ''}${r.responsavel ? ` · ${r.responsavel}` : ''}` : 'Escolha o dia e o horário'}
                meta={metaDe('reuniao')} {...obsDe('reuniao')} />
              <Linha feito={!!it('boas_vindas').status} titulo="Enviar boas-vindas" sub="Documento simples enviado pelo WhatsApp" meta={metaDe('boas_vindas')} {...obsDe('boas_vindas')}
                acoes={<button type="button" className={it('boas_vindas').status ? css.btnOk : css.btnPri} disabled={!pode} title={tituloBloqueio}
                  onClick={() => salvar('boas_vindas', { status: it('boas_vindas').status ? null : 'feito' })}>
                  {it('boas_vindas').status ? <><FiCheck size={12} /> Enviadas</> : 'Marcar como enviadas'}</button>} />
            </div>
          </div>
        </Secao>
      </>
    );
  }

  /* ── Etapa 2 ── */
  function etapa2() {
    const pessoas = it('pessoas_autorizadas').dados?.lista || [];
    const ass = it('contratos_assinados');
    const cob = it('cobranca');
    const lib = it('liberar_cliente');
    const itemLib = d.etapas.find(e => e.id === 2)?.itens.find(i => i.chave === 'liberar_cliente');
    const e = lead.endereco || {};
    return (
      <>
        <Secao titulo="Contato inicial">
          <Linha feito={!!it('contato_inicial').status} titulo="Confirmar as informações do lead" meta={metaDe('contato_inicial')} {...obsDe('contato_inicial')}
            acoes={<button type="button" className={it('contato_inicial').status ? css.btnOk : css.btnPri} disabled={!pode} title={tituloBloqueio}
              onClick={() => salvar('contato_inicial', { status: it('contato_inicial').status ? null : 'feito' })}>
              {it('contato_inicial').status ? <><FiCheck size={12} /> Confirmado</> : 'Confirmar'}</button>}>
            <div className={css.ficha}>
              <span><em>Responsável</em>{lead.nome || '—'}</span>
              <span><em>Telefone</em>{lead.telefone || '—'}</span>
              <span><em>E-mail</em>{lead.email || '—'}</span>
              <span><em>Código</em>{lead.codigo_cliente || '—'}</span>
              <span className={css.fichaLarga}><em>Endereço</em>{[e.logradouro, e.numero, e.bairro, e.municipio && `${e.municipio}/${e.uf || ''}`].filter(Boolean).join(', ') || '—'}</span>
              <span><em>Representante</em>{lead.representante?.nome || '—'}</span>
            </div>
          </Linha>
        </Secao>

        <Secao titulo="Contatos e pessoas autorizadas" sub="Quem pode interagir com dados sensíveis da empresa">
          <PessoasAutorizadas lista={pessoas} pode={pode} onSalvar={(lista) => salvar('pessoas_autorizadas', { status: lista.length ? 'feito' : null, dados: { lista } })} />
        </Secao>

        <Secao titulo="Contratos e cobrança">
          <div className={css.grade2}>
            <Linha feito={!!ass.status} titulo="Contratos assinados" meta={metaDe('contratos_assinados')} {...obsDe('contratos_assinados')}
              acoes={<Anexo chave="contratos_assinados" />}>
              <div className={css.campos}>
                <label className={css.campoMini}>Data da assinatura
                  <input type="date" className={css.input} disabled={!pode} value={ass.dados?.data || ''}
                    onChange={ev => salvar('contratos_assinados', { dados: { ...(ass.dados || {}), data: ev.target.value } })} />
                </label>
                <button type="button" className={ass.status ? css.btnOk : css.btnPri} disabled={!pode || (!ass.status && !ass.dados?.data)} title={tituloBloqueio}
                  onClick={() => salvar('contratos_assinados', { status: ass.status ? null : 'feito' })}>
                  {ass.status ? <><FiCheck size={12} /> Assinados</> : 'Contratos assinados'}</button>
              </div>
            </Linha>
            <Linha feito={!!cob.status} titulo="Primeira cobrança ao financeiro" meta={metaDe('cobranca')} {...obsDe('cobranca')}>
              <div className={css.campos}>
                <label className={css.campoMini}>Cobrança
                  <select className={css.input} disabled={!pode} value={cob.dados?.tipo || ''}
                    onChange={ev => salvar('cobranca', { dados: { ...(cob.dados || {}), tipo: ev.target.value, valor: cob.dados?.valor ?? (ev.target.value === 'mensal' ? lead.honorarios_mensais : lead.valor_servico_pontual) } })}>
                    <option value="">Selecione...</option>
                    <option value="pontual">{fluxo?.curto ? `Serviço pontual (${fluxo.curto})` : 'Serviço pontual'}</option>
                    <option value="mensal">Honorários mensais</option>
                  </select>
                </label>
                <span className={css.valor}>{moeda(cob.dados?.valor)}</span>
                <button type="button" className={cob.status ? css.btnOk : css.btnPri} disabled={!pode || (!cob.status && !cob.dados?.tipo)} title={tituloBloqueio}
                  onClick={() => salvar('cobranca', { status: cob.status ? null : 'feito', dados: { ...(cob.dados || {}), enviado_em: cob.status ? null : hojeIso() } })}>
                  {cob.status ? <><FiCheck size={12} /> Enviada</> : 'Registrar envio'}</button>
              </div>
            </Linha>
          </div>
        </Secao>

        <Secao titulo="Liberar cliente" sub="O cadastro e as rotinas foram criados bloqueados na aprovação do Leads">
          {itemLib?.automatico ? (
            <Linha feito titulo="Cliente já existia e não está bloqueado" sub="Fluxo de cliente ativo: nada a liberar" />
          ) : (
            <Linha feito={!!lib.status} titulo={lib.status ? 'Cliente liberado' : 'Cliente bloqueado'} meta={lib.status ? `Liberado por ${lib.feito_por || ''} em ${quando(lib.feito_em)}` : null}
              sub={!ass.status ? 'Registre os contratos assinados para poder liberar'
                : lib.status ? `Cadastro e rotinas liberados${lib.dados?.docs ? ` · Docs: ${lib.dados.docs.mensagem || lib.dados.docs.aviso || (lib.dados.docs.integrado === false ? 'integração não configurada' : 'enviado')}` : ''}`
                : 'Ao liberar, o cadastro e as rotinas ficam disponíveis e o cliente vai para o Docs'}
              acoes={
                <label className={`${css.chave} ${(!pode || !ass.status) ? css.chaveOff : ''}`} title={tituloBloqueio}>
                  <input type="checkbox" checked={!!lib.status} disabled={!pode || (!ass.status && !lib.status)}
                    onChange={ev => salvar('liberar_cliente', { status: ev.target.checked ? 'feito' : null })} />
                  <span className={css.chaveTrilho}><i /></span>
                  {lib.status ? <><FiUnlock size={13} /> Liberado</> : <><FiLock size={13} /> Liberar cliente</>}
                </label>
              } />
          )}
        </Secao>
      </>
    );
  }

  /* ── Etapa 3 ── */
  function etapa3() {
    const p = it('procuracao');
    const dec = p.dados?.decisao;
    const concluida = d.etapas.find(e => e.id === 3)?.concluida;
    return (
      <Secao titulo="Procuração RFB">
        <Linha feito={concluida} titulo="Gerar procuração RFB agora?" meta={metaDe('procuracao')} {...obsDe('procuracao')}
          sub={dec === 'sim' ? (concluida ? 'Procuração cadastrada no menu Procurações' : 'Cadastre a procuração no menu Procurações para concluir')
            : dec === 'nao' ? 'O cliente ficou "Pendente" em Procurações' : 'Ao concluir, o cliente fica "Pendente" em Certificados'}
          acoes={<>
            <div className={css.segmento}>
              {[['sim', 'Sim'], ['nao', 'Não']].map(([v, l]) => (
                <button key={v} type="button" disabled={!pode} title={tituloBloqueio} className={dec === v ? css.segOk : ''}
                  onClick={() => salvar('procuracao', { status: 'feito', dados: { decisao: v } })}>{l}</button>
              ))}
            </div>
            {dec === 'sim' && !concluida && d.cliente && (
              <button type="button" className={css.btnPri} onClick={() => navigate(`/app/clientes/procuracoes?cliente=${d.cliente.id}&tipo=RFB`)}>
                <FiExternalLink size={12} /> Abrir Procurações
              </button>
            )}
          </>} />
      </Secao>
    );
  }

  /* ── Etapa 4 ── */
  function etapa4() {
    const itens = d.etapas.find(e => e.id === 4)?.itens || [];
    return (
      <Secao titulo="Documentos da empresa" sub={lead.tipo_fluxo === 'baixa'
        ? 'Baixa: ao concluir, o cliente é inativado em todas as telas'
        : 'Ao concluir, o cliente fica "Pendente" no menu Licenciamento (licenças e alvará são tratados lá)'}>
        <div className={css.grade2}>
          {itens.map(i => {
            const x = it(i.chave);
            return (
              <Linha key={i.chave} feito={i.feito} titulo={i.nome} meta={metaDe(i.chave)} {...obsDe(i.chave)}
                sub={i.doLead && !x.status ? 'Recebido no Leads' : x.status === 'nao_se_aplica' ? i.opcional : null}
                acoes={<>
                  {i.doLead && !x.status && i.doLead.arquivo_path && (
                    <button type="button" className={css.btnLink} onClick={() => abrirArquivo(i.doLead.arquivo_path).catch(e2 => setErro(e2.message))}>
                      <FiFileText size={12} /> {i.doLead.arquivo_nome || 'do Leads'}
                    </button>
                  )}
                  {!i.doLead && <Anexo chave={i.chave} extra={{ status: 'recebido' }} />}
                  {!i.doLead && (
                    <button type="button" className={x.status === 'recebido' ? css.btnOk : css.btnSec} disabled={!pode} title={tituloBloqueio}
                      onClick={() => salvar(i.chave, { status: x.status === 'recebido' ? null : 'recebido' })}>{x.status === 'recebido' ? <><FiCheck size={12} /> Recebido</> : 'Recebido'}</button>
                  )}
                  {i.opcional && !i.doLead && (
                    <button type="button" className={x.status === 'nao_se_aplica' ? css.btnOk : css.btnSec} disabled={!pode} title={tituloBloqueio}
                      onClick={() => salvar(i.chave, { status: x.status === 'nao_se_aplica' ? null : 'nao_se_aplica' })}>{i.opcional}</button>
                  )}
                </>} />
            );
          })}
        </div>
      </Secao>
    );
  }

  const conteudo = { 1: etapa1, 2: etapa2, 3: etapa3, 4: etapa4 }[etapa.id];
  const ind = d.indicadores;
  return (
    <div className={css.fundo}>
      <div className={css.tela}>
        <header className={css.cabecalho}>
          <div className={css.cabIdent}>
            <h2>{lead.empresa || lead.nome}</h2>
            <div className={css.cabDados}>
              {lead.cnpj && <span>{lead.cnpj}</span>}
              <span className={css.seloFluxo}>{fluxo?.curto || 'Fluxo'}</span>
              {lead.regime_tributario && <span>{lead.regime_tributario}</span>}
              {lead.codigo_cliente && <span>Cód. {lead.codigo_cliente}</span>}
              <span>Responsável: {d.responsavel || '—'}</span>
              {d.cliente?.status === 'inativo' && <span className={css.indAlerta}>Cliente inativo</span>}
            </div>
          </div>
          <div className={css.indicadores}>
            <span className={ind.contratos === 'assinado' ? css.indOk : css.indPend}>Contratos: {ind.contratos}</span>
            <span className={ind.pagamento === 'enviado' ? css.indOk : css.indPend}>1º pagamento: {ind.pagamento}</span>
            <span className={ind.procuracao === 'cadastrada' ? css.indOk : css.indPend}>Procuração RFB: {ind.procuracao}</span>
            <span className={css.progresso} title="Progresso">
              <i style={{ width: `${d.progresso}%` }} /><b>{d.progresso}%</b>
            </span>
          </div>
          <div className={css.cabAcoes}>
            <button type="button" className={css.btnSec} onClick={onAbrirChecklist}><FiList size={13} /> Checklist anterior</button>
            <button type="button" className={css.btnFechar} onClick={onFechar} title="Fechar"><FiX size={18} /></button>
          </div>
        </header>

        <nav className={css.trilha}>
          {d.etapas.map((e, i) => (
            <button key={e.id} type="button" className={`${css.passo} ${e.id === etapa.id ? css.passoAtivo : ''} ${e.concluida ? css.passoFeito : ''}`}
              onClick={() => setAberta(e.id)}>
              <span className={css.passoNum}>{e.concluida ? <FiCheck size={12} /> : i + 1}</span>
              <span className={css.passoNome}>{e.titulo}</span>
              <span className={css.passoConta}>{e.itens.filter(x => x.feito).length}/{e.itens.length}</span>
            </button>
          ))}
        </nav>

        <div className={css.corpo}>
          {!pode && <p className={css.aviso}><FiLock size={13} /> Somente leitura: só o gerente executa o onboarding.</p>}
          {erro && <p className={css.erro}>{erro}</p>}
          <input ref={arquivoRef} type="file" hidden onChange={aoEscolherArquivo} />
          {conteudo()}
          <div className={css.rodapeEtapa}>
            <button type="button" className={css.btnSec} disabled={indice === 0} onClick={() => setAberta(d.etapas[indice - 1].id)}><FiChevronLeft size={14} /> Etapa anterior</button>
            <span>{etapa.concluida ? <><FiCheck size={13} /> Etapa concluída</> : `${etapa.itens.filter(x => x.feito).length} de ${etapa.itens.length} itens`}</span>
            <button type="button" className={css.btnSec} disabled={indice === d.etapas.length - 1} onClick={() => setAberta(d.etapas[indice + 1].id)}>Próxima etapa <FiChevronRight size={14} /></button>
          </div>
        </div>
      </div>
      {minutaAberta && <EditorMinuta minutaId={minutaAberta} onFechar={() => setMinutaAberta(null)} onMudou={carregar} />}
    </div>
  );
}

/* lista de pessoas autorizadas (nome, função, telefone, e-mail) */
function PessoasAutorizadas({ lista, pode, onSalvar }) {
  const vazio = { nome: '', funcao: '', telefone: '', email: '' };
  const [nova, setNova] = useState(vazio);
  const add = () => { if (!nova.nome.trim()) return; onSalvar([...lista, { ...nova, nome: nova.nome.trim() }]); setNova(vazio); };
  return (
    <div className={css.pessoas}>
      {lista.length > 0 && (
        <table className={css.tabela}>
          <thead><tr><th>Nome</th><th>Função</th><th>Telefone</th><th>E-mail</th><th /></tr></thead>
          <tbody>
            {lista.map((p, i) => (
              <tr key={i}>
                <td>{p.nome}</td><td>{p.funcao || '—'}</td><td>{p.telefone || '—'}</td><td>{p.email || '—'}</td>
                <td>{pode && <button type="button" className={css.btnIcone} title="Tirar" onClick={() => onSalvar(lista.filter((_, j) => j !== i))}><FiTrash2 size={12} /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {pode && (
        <div className={css.pessoaNova}>
          <input className={css.input} placeholder="Nome *" value={nova.nome} onChange={e => setNova(n => ({ ...n, nome: e.target.value }))} />
          <input className={css.input} placeholder="Função" value={nova.funcao} onChange={e => setNova(n => ({ ...n, funcao: e.target.value }))} />
          <input className={css.input} placeholder="Telefone" value={nova.telefone} onChange={e => setNova(n => ({ ...n, telefone: e.target.value }))} />
          <input className={css.input} placeholder="E-mail" value={nova.email} onChange={e => setNova(n => ({ ...n, email: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add())} />
          <button type="button" className={css.btnPri} onClick={add}><FiPlus size={13} /> Adicionar</button>
        </div>
      )}
      {!lista.length && !pode && <p className={css.vazio}>Ninguém registrado.</p>}
    </div>
  );
}

export default OnboardingTela;
